import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlayerAvatar } from '../components/PlayerPhoto';
import { X, Calendar, Youtube, Users } from 'lucide-react';
import { API, getLogoUrl } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import ShareButtons from '../components/ShareButtons';

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [completedGames, setCompletedGames] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState('roster');
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [playerGameStats, setPlayerGameStats] = useState([]);
  const { t, language } = useLanguage();

  const [seasonTab, setSeasonTab] = useState(2);

  useEffect(() => {
    setSelectedTeam(null);
    setTeams([]);
    axios.get(`${API}/teams?season=${seasonTab}`).then(res => setTeams(res.data)).catch(console.error);
    axios.get(`${API}/games?status=completed&season=${seasonTab}`).then(res => setCompletedGames(res.data)).catch(console.error);
  }, [seasonTab]);

  const getTeamGames = (teamId) => {
    return completedGames.filter(g => g.home_team_id === teamId || g.away_team_id === teamId);
  };

  // Dynamic subtitle based on team count
  const getSubtitle = () => {
    if (teams.length === 0) return t('teamsSubtitle');
    if (language === 'gr') {
      return `Γνωρίστε τις ${teams.length} ομάδες που αγωνίζονται στη Σεζόν ${seasonTab}`;
    }
    return `Meet the ${teams.length} teams competing in Season ${seasonTab}`;
  };

  const statColumns = [
    { key: 'games', label: language === 'gr' ? 'ΑΓ' : 'GP', title: language === 'gr' ? 'Αγώνες' : 'Games' },
    { key: 'points', label: language === 'gr' ? 'ΠΟΝ' : 'PTS', title: language === 'gr' ? 'Πόντοι' : 'Points' },
    { key: 'free_throws', label: language === 'gr' ? 'Β' : 'FT', title: language === 'gr' ? 'Εύστοχες Βολές' : 'Free Throws Made' },
    { key: 'two_pointers', label: '2Π', title: language === 'gr' ? 'Εύστοχα 2ποντα' : '2-Pointers Made' },
    { key: 'three_pointers', label: '3Π', title: language === 'gr' ? 'Εύστοχα 3ποντα' : '3-Pointers Made' },
    { key: 'def_rebounds', label: language === 'gr' ? 'ΑΡ' : 'DR', title: language === 'gr' ? 'Αμυντικά Ριμπάουντ' : 'Def Rebounds' },
    { key: 'off_rebounds', label: language === 'gr' ? 'ΕΡ' : 'OR', title: language === 'gr' ? 'Επιθετικά Ριμπάουντ' : 'Off Rebounds' },
    { key: 'total_rebounds', label: language === 'gr' ? 'ΣΡ' : 'REB', title: language === 'gr' ? 'Σύνολο Ριμπάουντ' : 'Total Rebounds' },
    { key: 'steals', label: language === 'gr' ? 'ΚΛ' : 'STL', title: language === 'gr' ? 'Κλεψίματα' : 'Steals' },
    { key: 'turnovers', label: language === 'gr' ? 'ΛΑ' : 'TO', title: language === 'gr' ? 'Λάθη' : 'Turnovers' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="teams-page">
      <div className="text-center mb-8">
        <h1 className="section-title mb-4">{t('teamsTitle')}</h1>
      </div>

      {/* Season Tabs */}
      <div className="flex justify-center gap-3 mb-8" data-testid="teams-season-tabs">
        <button onClick={() => setSeasonTab(1)} className={`px-5 py-2.5 rounded-lg font-semibold transition ${seasonTab === 1 ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="teams-season1-tab">
          {language === 'gr' ? 'Ομάδες 1ης Σεζόν' : 'Season 1 Teams'}
        </button>
        <button onClick={() => setSeasonTab(2)} className={`px-5 py-2.5 rounded-lg font-semibold transition ${seasonTab === 2 ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="teams-season2-tab">
          {language === 'gr' ? 'Ομάδες 2ης Σεζόν' : 'Season 2 Teams'}
        </button>
      </div>

      {teams.length > 0 ? (
      <>
      <p className="text-slate-600 text-center mb-8" data-testid="teams-subtitle">{getSubtitle()}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map(team => (
          <div key={team.id} className="card p-6 cursor-pointer hover-lift" onClick={() => { setSelectedTeam(selectedTeam?.id === team.id ? null : team); setActiveTab('roster'); }} data-testid={`team-card-${team.id}`}>
            {team.has_logo ? (
              <img src={getLogoUrl(team.id)} alt={team.name} className="w-20 h-20 rounded-full mx-auto mb-4 object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-2xl" style={{ backgroundColor: team.color }}>
                {team.short_name}
              </div>
            )}
            <h3 className="font-display font-bold text-xl text-[#1E3A5F] text-center mb-2">{team.name}</h3>
            <p className="text-slate-500 text-center text-sm mb-3">{team.coach ? `${t('coach')}: ${team.coach}` : ''}</p>
            <div className="flex justify-center gap-4 text-sm">
              <span className="text-green-600 font-bold">{team.wins || 0}W</span>
              <span className="text-red-500 font-bold">{team.losses || 0}L</span>
            </div>
            <p className="text-center text-slate-400 text-xs mt-2">{team.players?.length || 0} {t('players')}</p>
            {(team.top_scorer || team.top_rebounder) && (
              <div className="mt-1 space-y-0.5 text-center" data-testid={`team-leaders-card-${team.id}`}>
                {team.top_scorer && (
                  <p className="text-xs text-[#F97316] font-medium">
                    {language === 'gr' ? 'Πόντοι' : 'PTS'}: {team.top_scorer} ({team.top_scorer_points})
                  </p>
                )}
                {team.top_rebounder && (
                  <p className="text-xs text-[#1E3A5F] font-medium">
                    {language === 'gr' ? 'Ριμπ.' : 'REB'}: {team.top_rebounder} ({team.top_rebounder_rebounds})
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Team Details Modal with Tabs */}
      {selectedTeam && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTeam(null)} data-testid="team-modal">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 text-white" style={{ backgroundColor: selectedTeam.color }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {selectedTeam.has_logo && (
                    <img src={getLogoUrl(selectedTeam.id)} alt={selectedTeam.name} className="w-16 h-16 rounded-full object-cover border-2 border-white/30" />
                  )}
                  <div>
                    <h2 className="font-display font-bold text-2xl">{selectedTeam.name}</h2>
                    <p className="text-white/80">{selectedTeam.coach ? `${t('coach')}: ${selectedTeam.coach}` : ''}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedTeam(null)} className="text-white/80 hover:text-white"><X size={24} /></button>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="border-b border-slate-200">
              <div className="flex">
                <button 
                  onClick={() => setActiveTab('roster')}
                  className={`px-6 py-3 font-medium transition ${activeTab === 'roster' ? 'text-[#1E3A5F] border-b-2 border-[#F97316]' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {t('roster')}
                </button>
                <button 
                  onClick={() => setActiveTab('stats')}
                  className={`px-6 py-3 font-medium transition ${activeTab === 'stats' ? 'text-[#1E3A5F] border-b-2 border-[#F97316]' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {language === 'gr' ? 'Στατιστικά' : 'Statistics'}
                </button>
                <button 
                  onClick={() => setActiveTab('results')}
                  className={`px-6 py-3 font-medium transition ${activeTab === 'results' ? 'text-[#1E3A5F] border-b-2 border-[#F97316]' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {t('results')}
                </button>
              </div>
            </div>
            
            {/* Tab Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Roster Tab */}
              {activeTab === 'roster' && (
                <div>
                  <h3 className="font-bold text-lg mb-4">{t('roster')} ({selectedTeam.players?.length || 0} {t('players')})</h3>
                  <div className="space-y-3">
                    {selectedTeam.players?.map(player => (
                      <div key={player.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`player-${player.id}`}>
                        <div className="flex items-center gap-3">
                          <PlayerAvatar player={player} size={48} testId={`player-photo-${player.id}`} />
                          <span className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold" style={{ color: selectedTeam.color }}>
                            #{player.number}
                          </span>
                          <div>
                            <p className="font-medium">{player.name}</p>
                            <p className="text-xs text-slate-500">{[player.position, player.height].filter(Boolean).join(' • ')}</p>
                          </div>
                        </div>
                        {player.age && <span className="text-sm text-slate-500">{t('age')} {player.age}</span>}
                      </div>
                    ))}
                    {(!selectedTeam.players || selectedTeam.players.length === 0) && (
                      <p className="text-center text-slate-400 py-8">{language === 'gr' ? 'Δεν υπάρχουν παίκτριες' : 'No players'}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Stats Tab */}
              {activeTab === 'stats' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-3 font-semibold sticky left-0 bg-slate-100">#</th>
                        <th className="text-left p-3 font-semibold sticky left-10 bg-slate-100">{language === 'gr' ? 'Παίκτρια' : 'Player'}</th>
                        {statColumns.map(col => (
                          <th key={col.key} className="text-center p-3 font-semibold whitespace-nowrap" title={col.title}>
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTeam.players?.map((player, idx) => (
                        <React.Fragment key={player.id}>
                        <tr className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} cursor-pointer hover:bg-blue-50 transition`} onClick={async () => {
                          if (expandedPlayer === player.id) { setExpandedPlayer(null); return; }
                          setExpandedPlayer(player.id);
                          try {
                            const res = await axios.get(`${API}/player-game-stats/${player.id}`);
                            setPlayerGameStats(res.data);
                          } catch { setPlayerGameStats([]); }
                        }}>
                          <td className="p-3 font-medium sticky left-0" style={{ backgroundColor: idx % 2 === 0 ? (expandedPlayer === player.id ? '#eff6ff' : 'white') : (expandedPlayer === player.id ? '#eff6ff' : '#f8fafc') }}>{player.number}</td>
                          <td className="p-3 sticky left-10 whitespace-nowrap" style={{ backgroundColor: idx % 2 === 0 ? (expandedPlayer === player.id ? '#eff6ff' : 'white') : (expandedPlayer === player.id ? '#eff6ff' : '#f8fafc') }}>
                            {player.name}
                            {expandedPlayer === player.id ? <span className="ml-1 text-blue-500 text-xs">▲</span> : <span className="ml-1 text-slate-300 text-xs">▼</span>}
                          </td>
                          {statColumns.map(col => (
                            <td key={col.key} className="text-center p-3 whitespace-nowrap">
                              {player.stats?.[col.key] ?? 0}
                            </td>
                          ))}
                        </tr>
                        {expandedPlayer === player.id && (
                          <tr>
                            <td colSpan={statColumns.length + 2} className="p-0">
                              <div className="bg-blue-50 p-3 border-y border-blue-100">
                                {playerGameStats.length === 0 ? (
                                  <p className="text-sm text-slate-400 text-center py-2">{language === 'gr' ? 'Δεν υπάρχουν στατιστικά ανά αγώνα' : 'No per-game stats'}</p>
                                ) : (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="bg-blue-100/50">
                                          <th className="p-1.5 text-left">{language === 'gr' ? 'Αγ.' : 'MW'}</th>
                                          <th className="p-1.5 text-left">{language === 'gr' ? 'Αντίπαλος' : 'Opponent'}</th>
                                          {statColumns.filter(c => c.key !== 'games').map(col => (
                                            <th key={col.key} className="p-1.5 text-center">{col.label}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {playerGameStats.map(gs => (
                                          <tr key={gs.id} className="border-b border-blue-100/50">
                                            <td className="p-1.5 font-medium">{gs.week}</td>
                                            <td className="p-1.5 whitespace-nowrap">{gs.opponent}</td>
                                            {statColumns.filter(c => c.key !== 'games').map(col => (
                                              <td key={col.key} className="p-1.5 text-center">{gs.stats?.[col.key] ?? 0}</td>
                                            ))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {(!selectedTeam.players || selectedTeam.players.length === 0) && (
                    <p className="text-center text-slate-400 py-8">{language === 'gr' ? 'Δεν υπάρχουν παίκτριες' : 'No players'}</p>
                  )}
                </div>
              )}
              
              {/* Results Tab */}
              {activeTab === 'results' && (
                <div>
                  {getTeamGames(selectedTeam.id).length > 0 ? (
                    <div className="space-y-3">
                      {getTeamGames(selectedTeam.id).map(game => {
                        const isHome = game.home_team_id === selectedTeam.id;
                        const teamScore = isHome ? game.home_score : game.away_score;
                        const opponentScore = isHome ? game.away_score : game.home_score;
                        const won = teamScore > opponentScore;
                        const fmtDate = (() => { try { const p = game.date.split('-'); return `${p[2]}/${p[1]}/${p[0]}`; } catch { return game.date; } })();
                        return (
                          <div key={game.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`team-result-${game.id}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                                <Calendar size={14} /> {fmtDate}
                                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${won ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {won ? (language === 'gr' ? 'Ν' : 'W') : (language === 'gr' ? 'Η' : 'L')}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`font-semibold ${game.home_team_id === selectedTeam.id ? 'text-[#1E3A5F]' : 'text-slate-600'}`}>
                                  {game.home_team_name}
                                </span>
                                <span className="px-3 py-1 bg-white rounded font-bold text-sm">
                                  {game.home_score} - {game.away_score}
                                </span>
                                <span className={`font-semibold ${game.away_team_id === selectedTeam.id ? 'text-[#1E3A5F]' : 'text-slate-600'}`}>
                                  {game.away_team_name}
                                </span>
                              </div>
                            </div>
                            {game.video_url && (
                              <a href={game.video_url} target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-700">
                                <Youtube size={20} />
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 py-8">
                      {language === 'gr' ? 'Δεν υπάρχουν αποτελέσματα ακόμα' : 'No results yet'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      ) : (
        <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center" data-testid="teams-season2-empty">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-display font-bold text-slate-400 mb-2">
            {language === 'gr' ? 'Σύντομα κοντά σας!' : 'Coming Soon!'}
          </h2>
          <p className="text-slate-400 max-w-md mx-auto">
            {language === 'gr'
              ? 'Οι ομάδες της 2ης Σεζόν δεν έχουν ανακοινωθεί ακόμα. Μείνετε συντονισμένοι!'
              : 'Season 2 teams have not been announced yet. Stay tuned!'}
          </p>
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <ShareButtons title={language === 'gr' ? 'Ομάδες WBLA' : 'WBLA Teams'} />
      </div>
    </div>
  );
};

export default TeamsPage;
